import { useEffect, useState } from "react";

import { Button, Divider, FormControl, FormErrorMessage, FormLabel, Input, Switch, useDisclosure, useToast } from "@chakra-ui/react";
import { Field, FieldProps, Form, Formik } from "formik";
import { useLocation, useNavigate } from "react-router-dom";
import { HttpStatusCode } from "axios";

import ArrowIcon from "@/assets/ArrowIcon";
import { COLORS, DURATION_TOAST } from "@/common/constants";
import ModalConfirmation from "@/components/Modals/ModalConfirmation";
import SelectCompanyType from "@/components/Selects/SelectCompanyType";
import CurrencyInput from "@/components/Fields/CurrencyInput";
import { useAppSelector } from "@/redux/hooks";
import { RootState } from "@/redux/store";
import { inspectionFeeSchema } from "@/schemas/inspectionFeeSchema";
import { getInspectionFeeById, postInspectionFee, putInspectionFee } from "@/services/inspectionFeeService";
import { parseCurrencyFloat } from "@/utils/currency";

import '../styles/Form.css';

const EditInspectionFee = () => {
    const navigate = useNavigate();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const location = useLocation();
    const toast = useToast();
    const name = useAppSelector((state: RootState) => state.account.name);

    const { id } = location.state || {};

    const [confirmationText, setConfirmationTest] = useState("");
    const [confirmButtonText, setConfirmButtonText] = useState("");
    const [cancelButtonText, setCancelButtonText] = useState("");
    const [dataDetail, setDataDetail] = useState(null) as any;
    const [dataEdit, setDataEdit] = useState(null) as any;
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [title, setTitle] = useState("");

    const handleCancel = () => {
        onOpen();
        setTitle("Cancel Changes?");
        setConfirmationTest("Are you sure you want to discard all changes?");
        setConfirmButtonText("Yes");
        setCancelButtonText("No");
    }

    const handleSubmit = async (data: any) => {
        setTitle("Save Inspection Fee?");
        setConfirmationTest("Are you sure you want to save?");
        setConfirmButtonText("Save Inspection Fee");
        setCancelButtonText("Cancel");
        onOpen();
        setDataEdit(data);
        setIsSubmitting(true);
    }

    const generateDataEdit = () => {
        const obj = {
            name: dataEdit.name,
            type: dataEdit.type,
            price: dataEdit.price ? parseCurrencyFloat(dataEdit.price) : 0,
            fee: dataEdit.fee ? parseCurrencyFloat(dataEdit.fee) : 0,
            addCost: dataEdit.addCost,
            status: !id || dataEdit.status, // if no ID then always true, otherwise follow status
        };
        if (id){
            return {...obj, id, modifiedBy: (name || "System")}
        } else {
            return obj;
        }
    }

    const handleConfirm = async () => {
        onClose();

        if (title.startsWith("Cancel")) {
            //navigate('/manage-inspection-fee');
            history.back();
            return;
        }

        const obj = generateDataEdit();
        let response
        if (id){
            response = await putInspectionFee(obj);
        } else {
            response = await postInspectionFee(obj);
        }
        setIsSubmitting(false);
        if (response.status === HttpStatusCode.Ok) {
            toast({
                title: 'Inspection fee saved.',
                description: "We've updated inspection fee for you.",
                status: 'success',
                duration: DURATION_TOAST,
                isClosable: true,
            });

            setTimeout(() => {
                navigate('/manage-inspection-fee')
            }, 1000);
        } else {
            toast({
                title: response?.code || response?.status || 'Inspection fee failed to save.',
                description: response?.message || "We could not save the inspection fee for you.",
                status: 'error',
                duration: DURATION_TOAST,
                isClosable: true,
            });
        }
    }

    const handleConfirmToCancel = () => {
        setIsSubmitting(false);
        onClose();
    }

    const getDataDetail = async () => {
        const response = await getInspectionFeeById(id);
        if (response) {
            setDataDetail(response.data);
        }
    }

    useEffect(() => {
        if (id) {
            getDataDetail();
        }
    }, [id])

    return (
        <>
            <ModalConfirmation
                isOpen={isOpen} onClose={onClose}
                handleCancel={handleConfirmToCancel} handleConfirm={handleConfirm}
                title={title} confirmationText={confirmationText}
                confirmButtonText={confirmButtonText} cancelButtonText={cancelButtonText}
            />
            <div className="form-container">
                <Formik
                    initialValues={{
                        type: (dataDetail?.type ?? '') as string,
                        name: (dataDetail?.name ?? '') as string,
                        price: (dataDetail?.price ?? 0) as number,
                        fee: (dataDetail?.fee ?? 0) as number,
                        addCost: Boolean(dataDetail?.addCost),
                        status: Boolean(dataDetail?.status),
                    }}
                    validationSchema={inspectionFeeSchema}
                    onSubmit={(values) => {
                        handleSubmit(values);
                    }}
                    enableReinitialize={true}
                >
                    {(props) => (
                        <Form>
                            <Field name='type'>
                                {({ form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.type && form.touched.type)} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Type</FormLabel>
                                            <div className="form-input-container">
                                                <SelectCompanyType name='type' placeholder="Choose Type" handleChange={({ value }: any) => props.setFieldValue('type', value)} value={props.values.type} hasError={form.errors.type && form.touched.type} defaultValue={props.values.type} />
                                                <FormErrorMessage>{String(form.errors.type)}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='name'>
                                {({ field, form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.name && form.touched.name)} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Name<span style={{ color: 'red' }}>*</span></FormLabel>
                                            <div className="form-input-container">
                                                <Input {...field} placeholder='Input Name' className="form-input" />
                                                <FormErrorMessage>{String(form.errors.name)}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='price'>
                                {({ field, form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.price && form.touched.price)} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Price<span style={{ color: 'red' }}>*</span></FormLabel>
                                            <div className="form-input-container">
                                                <CurrencyInput
                                                    {...field}
                                                    placeholder='Input Price'
														prefix={props.values.type === 'SIRE' ? 'USD' : 'IDR'}
                                                    value={props.values.price}
                                                    error={Boolean(form.errors.price && form.touched.price)}
                                                    onValueChange={({ floatValue = 0 }) => props.setFieldValue('price', floatValue)}
                                                    disabled={isSubmitting}
                                                    width='100%'
                                                />
                                                <FormErrorMessage>{String(form.errors.price)}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='fee'>
                                {({ field, form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.fee && form.touched.fee)} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Fee</FormLabel>
                                            <div className="form-input-container">
                                                <CurrencyInput
                                                    {...field}
                                                    placeholder='Input Fee'
														prefix={props.values.type === 'SIRE' ? 'USD' : 'IDR'}
                                                    value={props.values.fee}
                                                    error={Boolean(form.errors.fee && form.touched.fee)}
                                                    onValueChange={({ floatValue = 0 }) => props.setFieldValue('fee', floatValue)}
                                                    disabled={isSubmitting}
                                                    width="100%"
                                                />
                                                <FormErrorMessage>{String(form.errors.fee)}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='addCost'>
                                {({ field, form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.addCost && form.touched.addCost)} style={{ marginBottom: 15, alignItems: 'center' }}>
                                        <div className="form-field-container">
                                            <FormLabel htmlFor='add-cost-switch' mb='0' className="form-label">
                                                Add Cost
                                            </FormLabel>
                                            <div className="form-input-container">
                                                <Switch {...field} isChecked={props.values.addCost} onChange={(e) => props.setFieldValue('addCost', e.target.checked)} id='add-cost-switch' style={{ width: '50%', marginTop: '5px', backgroundColor: 'white' }} />
                                            </div>
                                            
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            {Boolean(id) && <Field name='status'>
                                {({ field, form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.status && form.touched.status)} style={{ marginBottom: 15, alignItems: 'center' }}>
                                        <div className="form-field-container">
                                            <FormLabel htmlFor='status-switch' mb='0' className="form-label">
                                                Status
                                            </FormLabel>
                                            <div className="form-input-container">
                                                <Switch {...field} isChecked={props.values.status} onChange={(e) => props.setFieldValue('status', e.target.checked)} id='status-switch' style={{ width: '50%', marginTop: '5px', backgroundColor: 'white' }} />
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>}
                            <Divider />
                            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <Button
                                    mt={4}
                                    bgColor='white'
                                    borderColor='#D0D5DD'
                                    isLoading={isSubmitting}
                                    marginRight={5}
                                    onClick={handleCancel}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    mt={4}
                                    bgColor={COLORS.BUTTON.BACKGROUND}
                                    color={COLORS.BUTTON.FOREGROUND}
                                    isLoading={isSubmitting}
                                    type='submit'
                                    rightIcon={<ArrowIcon />}
                                >
                                    Save Fee
                                </Button>
                            </div>
                        </Form>
                    )}
                </Formik>
            </div>
        </>
    )
}

export default EditInspectionFee;
